import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import kebabCase from 'lodash/kebabCase.js';
import React, { useRef, useState, useEffect } from 'react';
import { SelectionLabel } from '../../core/components/SelectionControl/label.js';
import { Checkbox } from '../Form/Controls/Checkbox/component.js';
import { pickDataAttributes } from '../../utils/pickDataAttributes.js';
import { formatClassName as _formatClassName } from '../../utils/formatClassName.js';
var _excluded = ["chakra", "checked", "className", "containerProps", "defaultChecked", "disabled", "hintText", "id", "label", "leftToggle", "onChange", "size"];
/**
 * React Toggle Checkbox components
 * @param {object}  props.defaultChecked   Initial checked state - can be overriden
 * @param {object}  props.checked          Uses onChange to override default behaviour
 * @param {string}  props.className        Layout design level customization - size: sm, lg, xl or round
 *
 * @example <caption>Usage: </caption>
 * //Default values
 * <ToggleCheckbox
 *    defaultChecked={true}
 *    size="sm"
 * />
 *
 * //Round large toggle checkbox with checked value override
 * <ToggleCheckbox
 *    checked={this.state.isChecked}
 *    onChange={() => this.setState({ isChecked: !this.state.isChecked} )} //Bad setstate example
 *    size="lg"
 * />
 */
var ToggleCheckbox = function ToggleCheckbox(props) {
  var _props$chakra = props.chakra,
    chakra = _props$chakra === void 0 ? 'on' : _props$chakra,
    checked = props.checked,
    _props$className = props.className,
    className = _props$className === void 0 ? '' : _props$className,
    containerProps = props.containerProps,
    _props$defaultChecked = props.defaultChecked,
    defaultChecked = _props$defaultChecked === void 0 ? false : _props$defaultChecked,
    disabled = props.disabled,
    hintText = props.hintText,
    id = props.id,
    label = props.label,
    _props$leftToggle = props.leftToggle,
    leftToggle = _props$leftToggle === void 0 ? false : _props$leftToggle,
    onChange = props.onChange,
    _props$size = props.size,
    size = _props$size === void 0 ? 'sm' : _props$size,
    inputProps = _objectWithoutProperties(props, _excluded);
  var input = useRef(null);
  var _useState = useState(checked !== null && checked !== void 0 ? checked : defaultChecked),
    _useState2 = _slicedToArray(_useState, 2),
    checkedState = _useState2[0],
    setChecked = _useState2[1];
  useEffect(function () {
    if (checked !== undefined) {
      setChecked(!!checked);
    }
  }, [checked]);

  /**
   * Click event handler for toggle checkbox
   * @param {object} event  Event
   */
  var handleClick = function handleClick(event) {
    var checkbox = input.current;
    if (event.target !== checkbox) {
      event.preventDefault();
      checkbox.focus();
      checkbox.click();
      return;
    }
    setChecked(checked !== undefined ? checked : checkbox.checked);
  };
  var handleChakraClick = function handleChakraClick(event) {
    setChecked(!checkedState);
    if (typeof onChange === 'function') {
      onChange(event);
    }
    if (typeof (inputProps === null || inputProps === void 0 ? void 0 : inputProps.onClick) === 'function') {
      inputProps.onClick(event);
    }
  };
  var dataAttributes = pickDataAttributes(props, 'toggle-checkbox');
  if (!dataAttributes['data-action']) {
    dataAttributes['data-action'] = label ? "toggle-".concat(kebabCase(label)) : 'toggle';
  }
  var classes = _formatClassName([className, 'round', size]);
  var labelClasses = _formatClassName(['toggle-checkbox-label', leftToggle ? 'm-l-10' : 'm-r-10']);
  var toggle = /*#__PURE__*/React.createElement("div", _extends({
    className: "toggle--checkbox ".concat(classes, " ").concat(disabled && 'disabled'),
    onClick: handleClick
  }, dataAttributes), /*#__PURE__*/React.createElement("input", _extends({}, inputProps, {
    checked: checkedState,
    disabled: disabled,
    className: "toggle--check-input",
    ref: input,
    type: "checkbox",
    value: "true"
  })), /*#__PURE__*/React.createElement("span", {
    className: "toggle--slider"
  }), inputProps.name && /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    name: inputProps.name,
    value: "".concat(!!checkedState)
  }));
  if (chakra === 'off' && !label) {
    return toggle;
  }
  if (chakra === 'off') {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", _extends({
      className: "toggle-checkbox-container"
    }, containerProps), leftToggle && toggle, /*#__PURE__*/React.createElement("label", {
      className: labelClasses,
      htmlFor: id
    }, label), !leftToggle && toggle), hintText && /*#__PURE__*/React.createElement("div", {
      className: "toggle-checkbox-hint-text m-t-5"
    }, hintText));
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Checkbox, _extends({}, dataAttributes, {
    isChecked: checkedState,
    isDisabled: disabled,
    name: inputProps.name,
    onChange: handleChakraClick,
    type: "toggle",
    value: String(checked),
    toggleLabelSide: leftToggle ? 'right' : 'left'
  }), /*#__PURE__*/React.createElement(SelectionLabel, {
    label: label
  })), hintText && /*#__PURE__*/React.createElement("div", {
    className: "toggle-checkbox-hint-text"
  }, hintText));
};
export { ToggleCheckbox };